import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { WorkspaceSearcher } from '@/features/iam/workspaces/WorkspaceSearcher'
import { Id } from '@/infra/id'
import {
  WorkspaceSelectorViewHeading,
  type WorkspaceSelectorViewHeadingProps,
} from '@/features/iam/workspaces/components/WorkspaceSelectorViewHeading'

interface WorkspaceSelectorViewHeadingContainerProps extends Omit<
  WorkspaceSelectorViewHeadingProps,
  | 'options'
  | 'isAllSelected'
  | 'secondaryText'
  | 'onSelect'
  | 'onSelectAll'
  | 'search'
> {
  workspaces: Workspace[]
  value: Workspace
  onChange: (workspace: Workspace) => unknown
  searcher?: WorkspaceSearcher
  renderName?: (workspace: Workspace) => ReactNode
}

export const WorkspaceSelectorViewHeadingContainer = observer(
  function WorkspaceSelectorViewHeadingContainer({
    workspaces,
    value,
    onChange,
    searcher,
    renderName,
    allOptionText,
    alwaysUseDropdown = false,
    ...rest
  }: WorkspaceSelectorViewHeadingContainerProps) {
    return (
      <WorkspaceSelectorViewHeading
        {...rest}
        allOptionText={allOptionText}
        alwaysUseDropdown={alwaysUseDropdown}
        secondaryText={getSecondaryText(
          workspaces,
          value,
          allOptionText,
          alwaysUseDropdown,
        )}
        options={workspaces.map((workspace) => ({
          id: Id.assert(workspace.id),
          label: renderName ? renderName(workspace) : workspace.name,
          isSelected: workspace === value,
        }))}
        isAllSelected={value === Workspace.none}
        onSelect={(id) => {
          const workspace = workspaces.find((w) => w.id === id)
          if (workspace) {
            onChange(workspace)
          }
        }}
        onSelectAll={() => onChange(Workspace.none)}
        search={
          searcher && {
            text: searcher.searchText,
            onTextChange: searcher.setSearchText,
            isOpen: searcher.editing,
            onOpen: searcher.startEditing,
            onClose: searcher.stopEditing,
          }
        }
      />
    )
  },
)

function getSecondaryText(
  workspaces: Workspace[],
  value: Workspace,
  allOptionText: string | null,
  alwaysUseDropdown: boolean,
) {
  if (value !== Workspace.none) {
    return value.name
  }

  if (allOptionText === null) {
    return workspaces.length > 0 ? value.name : 'No workspaces available'
  }

  if (workspaces.length === 1 && !alwaysUseDropdown) {
    return workspaces[0].name
  }

  return allOptionText
}
